import { GSTIN_RE } from "@/lib/gstin";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Search, Info, Upload, AlertCircle, CheckCircle2 } from "lucide-react";
import { api, listFxRates, ACCEPTED_LOGO_TYPES, MAX_LOGO_BYTES, getWarehouses, warehouseOptionLabel } from "@/services/api";
import type { Warehouse } from "@/services/api";
import {
  ReferenceLoading, ReferenceError, ReferenceEmpty, type LoadStatus,
} from "@/components/ReferenceDataStates";
import { derivedOptions } from "@/lib/filterOptions";
import type { BackendCompanySettings } from "@/services/api";
import { useCanEditSettings, useIsAdminOrFalse } from "@/auth/AuthContext";
import { FxRatesTable, statusOf as fxStatusOf } from "@/components/FxRatesTable";
import { PublishFxRate } from "@/components/PublishFxRate";
import { CorrectFxRateDialog } from "@/components/CorrectFxRate";
import { rateInForce, todayIso } from "@/services/fxInForce";
import { UsersTab } from "@/components/UsersTab";
import { DutyRatesTab } from "@/components/DutyRatesTab";
import { WarehousesTab } from "@/components/WarehousesTab";
import { HolidaysTab } from "@/components/HolidaysTab";
import { FxRateDetailPanel } from "@/components/FxRateDetailPanel";
import type { FxRate, CompanySettings } from "@/types";
import { formatDate } from "@/lib/format";
import { colors, semanticColors, radius, spacing, heights, layout, typography, iconSize, iconStroke, shadows } from "@/styles/tokens";
import { READ_ONLY_NOTE } from "@/components/ReadOnlySave";

// All reads through services/api.ts. Everything on this page is a stored
// setting only — no calculation happens here, and none of it (buffers, FX
// rates, company info) is wired into quote pricing yet. See CLAUDE.md's
// "Pricing calculations" hard rule.

type Tab = "fx" | "duty" | "warehouses" | "holidays" | "buffers" | "company" | "users";

// ⚠️ CURRENCY AND SOURCE ARE DERIVED FROM THE LOADED ROWS NOW, not listed
// here. They used to be literal arrays -- ["All","USD","EUR","CNY"] and the
// two source labels -- while the rows themselves came from a mock that
// happened to match. Both are master data on the backend (res.currency, and
// an Odoo Selection), so a literal list goes stale in the silent direction:
// the filter would simply never offer a value, and the rows it would have
// matched would look like they did not exist. See lib/filterOptions.ts.
//
// STATUS STAYS A LITERAL, deliberately: it is the RANGE OF statusOf -- a
// verdict computed from `active` -- not a column any row holds. Deriving it
// would drop "Superseded" from the filter on a day when nothing happened to
// be superseded, which is exactly when somebody would look.
const STATUS_OPTS   = ["All", "Active", "Superseded"];

// What a Viewer or a Sales user is told on the Company tab.
//
// ⚠️ IT NAMES THE ROLE THAT CAN, rather than only saying "you cannot". A
// person who reads "read-only" and nothing else has to go and ask someone
// what to do next; a person who reads "an Anixify Admin can change these"
// knows who to ask. The top bar already shows their own role beside their
// name for the same reason.
//
// It borrows READ_ONLY_NOTE's opening words so this and the seven disabled
// Save buttons elsewhere in the app say the same thing.
const NON_ADMIN_NOTE =
  `${READ_ONLY_NOTE} for your role — an Anixify Admin can change these details.`;

const SELECT_STYLE: React.CSSProperties = {
  height: heights.input, padding: `0 ${spacing[8]}px`, fontSize: typography.fontSize.base,
  color: semanticColors.textPrimary, backgroundColor: semanticColors.surface,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  outline: "none",
};
const LABEL: React.CSSProperties = {
  fontSize: typography.fontSize.xs, color: semanticColors.textMuted,
  textTransform: "uppercase", letterSpacing: typography.letterSpacing.wide,
  fontWeight: typography.fontWeight.medium, marginBottom: spacing[4],
};
// The GSTIN format rule lives in lib/gstin.ts, shared with the customer form.

const INPUT: React.CSSProperties = {
  width: "100%", padding: `${spacing[6]}px ${spacing[10]}px`, height: heights.input,
  fontSize: typography.fontSize.base, color: semanticColors.textPrimary,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  backgroundColor: semanticColors.surface, outline: "none",
  boxSizing: "border-box",
};

function FieldLabel({ children }: { children: React.ReactNode }) {
  return <span style={LABEL}>{children}</span>;
}
// ⚠️ readOnly, NOT disabled. A disabled input is skipped by keyboard
// navigation and is not read out by screen readers, so a Viewer would find
// the company details unreachable rather than merely uneditable -- and these
// are exactly the details somebody with a Viewer role has a legitimate reason
// to look up. readOnly keeps the value selectable and copyable, which is what
// "read-only" should mean.
const READONLY_INPUT: React.CSSProperties = {
  ...INPUT,
  backgroundColor: semanticColors.panelBg,
  color: semanticColors.textSecondary,
  cursor: "default",
};

function TextField({
  label, testid, value, onChange, readOnly = false,
}: {
  label: string; testid: string; value: string;
  onChange: (v: string) => void;
  readOnly?: boolean;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column" }}>
      <FieldLabel>{label}</FieldLabel>
      <input data-testid={testid} type="text" value={value}
             readOnly={readOnly}
             aria-readonly={readOnly || undefined}
             onChange={(e) => onChange(e.target.value)}
             style={readOnly ? READONLY_INPUT : INPUT} />
    </div>
  );
}

function InfoBar({ testid, children }: { testid: string; children: React.ReactNode }) {
  return (
    <div
      data-testid={testid}
      className="flex items-center"
      style={{
        minHeight: heights.control, padding: `${spacing[6]}px ${spacing[12]}px`, marginBottom: spacing[12], flexShrink: 0,
        backgroundColor: colors.indigo50,
        border: `1px solid ${colors.indigo100}`, borderRadius: radius.base,
        gap: spacing[8], fontSize: typography.fontSize.sm, color: semanticColors.textSecondary,
      }}
    >
      <Info size={iconSize.md} strokeWidth={iconStroke.regular} color={colors.indigo600} />
      <span>{children}</span>
    </div>
  );
}

export default function SettingsPage() {
  const [tab, setTab] = useState<Tab>("fx");
  // ⚠️ COSMETIC, like every role check in this app: the route refuses Sales
  // and Viewer with a 403 whatever this answers. It only stops the screen
  // offering an action that would fail.
  const canPublishFx = useIsAdminOrFalse();

  // ⚠️ THE FX TABLE COMES OUT OF THE DATABASE. It rendered mocks/fxRates.ts
  // until this slice, while anixify.quote.line resolved an imported line's
  // conversion rate out of the REAL anixify.fx.rate -- so the rate on this
  // screen and the rate in the price were different numbers.
  const [fxRates, setFxRates] = useState<FxRate[]>([]);
  const [fxLoadStatus, setFxLoadStatus] = useState<LoadStatus>("loading");
  const [fxLoadError, setFxLoadError] = useState("");

  const [fxSearch, setFxSearch] = useState("");
  const [fxCurrency, setFxCurrency] = useState("All");
  const [fxSource, setFxSource] = useState("All");
  const [fxStatus, setFxStatus] = useState("All");
  const [selectedFxRate, setSelectedFxRate] = useState<FxRate | null>(null);
  // settings-fx-layout: the row being corrected (the dialog), and the
  // confirmation left on the page once it is closed.
  const [correcting, setCorrecting] = useState<FxRate | null>(null);
  const [correctDone, setCorrectDone] = useState("");

  // `refresh` = re-read after a publish or a correction. The status stays
  // "ok" while it runs: the Publish and Correct forms render only on "ok", so
  // flipping to "loading" unmounted them and their "Published…"/"Corrected…"
  // confirmation was never seen (found by the staging browser tests,
  // 2026-09-24). A failed re-read still clears to "error", as before.
  const loadFxRates = useCallback(async (refresh = false) => {
    if (!refresh) setFxLoadStatus("loading");
    setFxLoadError("");
    const result = await listFxRates();
    if (!result.ok) {
      // Cleared, never left stale and never replaced by a mock. A plausible
      // exchange rate under a failed read is the disagreement this cutover
      // exists to end, restated as a lie.
      setFxRates([]);
      setSelectedFxRate(null);
      setFxLoadError(result.error ?? "Backend reported an unknown failure.");
      setFxLoadStatus("error");
      return;
    }
    setFxRates(result.rates ?? []);
    setFxLoadStatus("ok");
  }, []);

  useEffect(() => { loadFxRates(); }, [loadFxRates]);

  const fxCurrencyOptions = useMemo(
    () => derivedOptions(fxRates, (r) => r.currency), [fxRates],
  );
  const fxSourceOptions = useMemo(
    () => derivedOptions(fxRates, (r) => r.source), [fxRates],
  );

  // A selection the loaded rows can no longer satisfy filters to nothing and
  // reads as an empty result rather than a stale control.
  useEffect(() => {
    if (fxCurrency !== "All" && !fxCurrencyOptions.includes(fxCurrency)) setFxCurrency("All");
  }, [fxCurrencyOptions, fxCurrency]);
  useEffect(() => {
    if (fxSource !== "All" && !fxSourceOptions.includes(fxSource)) setFxSource("All");
  }, [fxSourceOptions, fxSource]);

  const filteredFxRates = useMemo(() => {
    const q = fxSearch.trim().toLowerCase();
    return fxRates.filter((r) => {
      if (fxCurrency !== "All" && r.currency !== fxCurrency) return false;
      if (fxSource   !== "All" && r.source   !== fxSource)   return false;
      const s = fxStatusOf(r);
      if (fxStatus !== "All" && s !== fxStatus) return false;
      if (q.length > 0 && !r.notes.toLowerCase().includes(q)) return false;
      return true;
    });
  }, [fxRates, fxSearch, fxCurrency, fxSource, fxStatus]);

  return (
    <div data-testid="settings-page" className="flex" style={{ minHeight: `calc(100dvh - ${heights.topBar}px)` }}>
      <div className="flex flex-col" style={{ padding: spacing[24], flex: 1, minWidth: 0 }}>
        <div style={{ marginBottom: spacing[16] }}>
          <div data-testid="settings-title"
               style={{ fontSize: typography.fontSize.xxl, fontWeight: typography.fontWeight.semibold, color: semanticColors.textPrimary, letterSpacing: typography.letterSpacing.tight }}>
            Settings
          </div>
          <div data-testid="settings-subtitle"
               style={{ fontSize: typography.fontSize.base, color: semanticColors.textSecondary, marginTop: spacing[4] }}>
            Exchange rates, risk buffers, and company details
          </div>
        </div>

        <div data-testid="settings-tabs"
             className="flex items-center"
             // settings-fx-layout: the page flows in <main>'s scroll; the tab row
             // scrolls sideways on its own at phone width rather than widening the page.
             style={{ borderBottom: `1px solid ${semanticColors.border}`, marginBottom: spacing[16], gap: spacing[24],
                      overflowX: "auto", overflowY: "hidden", whiteSpace: "nowrap" }}>
          {/* Users is ADMIN ONLY (settings-users-tab): hidden for Sales and
              Viewer, and /anixify/api/users refuses them regardless. */}
          {/* Duty Rates is ADMIN ONLY too (duty-rate-entry): hidden for Sales
              and Viewer, and every duty write route refuses them regardless. */}
          {/* Holidays (holiday-list, M1): every role reads; the server lets only an Admin write. */}
          {((canPublishFx ? ["fx", "duty", "warehouses", "holidays", "buffers", "company", "users"] : ["fx", "warehouses", "holidays", "buffers", "company"]) as Tab[]).map((t) => {
            const active = tab === t;
            const label = t === "fx" ? "Exchange Rates" : t === "duty" ? "Duty Rates" : t === "warehouses" ? "Warehouses"
              : t === "holidays" ? "Holidays"
              : t === "buffers" ? "Risk Buffers" : t === "company" ? "Company" : "Users";
            return (
              <button key={t} data-testid={`tab-${t}`} data-active={active ? "true" : "false"}
                      onClick={() => setTab(t)}
                      style={{
                        background: "transparent", border: "none",
                        padding: `${spacing[10]}px ${spacing[2]}px`, cursor: "pointer",
                        fontSize: typography.fontSize.base,
                        color: active ? semanticColors.textPrimary : semanticColors.textMuted,
                        fontWeight: active ? typography.fontWeight.medium : typography.fontWeight.regular,
                        borderBottom: `2px solid ${active ? colors.indigo600 : "transparent"}`,
                        marginBottom: "-1px",
                      }}>
                {label}
              </button>
            );
          })}
        </div>

        {tab === "fx" && (
          <ExchangeRatesTab
            rates={filteredFxRates}
            allRates={fxRates}
            canPublish={canPublishFx}
            onPublished={() => { void loadFxRates(true); }}
            onCorrect={canPublishFx ? (r) => { setCorrectDone(""); setCorrecting(r); } : undefined}
            correctDone={correctDone}
            totalCount={fxRates.length}
            loadStatus={fxLoadStatus}
            loadError={fxLoadError}
            onRetry={() => { void loadFxRates(); }}
            currencyOptions={fxCurrencyOptions}
            sourceOptions={fxSourceOptions}
            search={fxSearch} onSearch={setFxSearch}
            currency={fxCurrency} onCurrency={setFxCurrency}
            source={fxSource} onSource={setFxSource}
            status={fxStatus} onStatus={setFxStatus}
            selectedId={selectedFxRate?.id}
            onRowClick={setSelectedFxRate}
          />
        )}
        {tab === "duty" && canPublishFx && <DutyRatesTab />}
        {/* warehouse-master: every role sees the list; only an Admin edits. */}
        {tab === "warehouses" && <WarehousesTab />}
        {tab === "holidays" && <HolidaysTab />}
        {tab === "buffers" && <RiskBuffersTab />}
        {tab === "company" && <CompanyTab />}
        {tab === "users" && canPublishFx && <UsersTab />}
      </div>

      {/* Sticky, so the panel stays in view while the history scrolls with
          the page (the page no longer has a fixed height). */}
      {tab === "fx" && selectedFxRate && (
        <div style={{ position: "sticky", top: 0, alignSelf: "flex-start",
                      height: `calc(100dvh - ${heights.topBar}px)` }}>
          <FxRateDetailPanel rate={selectedFxRate} onClose={() => setSelectedFxRate(null)} />
        </div>
      )}
      {/* The correction dialog (settings-fx-layout): opened from a row's
          "Correct", which the table offers only on server-correctable rows. */}
      {correcting && (
        <CorrectFxRateDialog
          rate={correcting}
          onClose={() => setCorrecting(null)}
          onCorrected={(msg) => { setCorrecting(null); setCorrectDone(msg); void loadFxRates(true); }}
        />
      )}
    </div>
  );
}

// ---------- Tab 1: Exchange Rates -------------------------------------------

function ExchangeRatesTab({
  rates, allRates, canPublish, onPublished, onCorrect, correctDone,
  totalCount, loadStatus, loadError, onRetry,
  currencyOptions, sourceOptions,
  search, onSearch,
  currency, onCurrency,
  source, onSource,
  status, onStatus,
  selectedId, onRowClick,
}: {
  rates: FxRate[];
  /** The WHOLE schedule: the cards and the publish typo guard read the rate
   *  in force today, and a filter must not be able to hide it. */
  allRates: FxRate[];
  canPublish: boolean;
  onPublished: () => void;
  onCorrect?: (r: FxRate) => void;
  correctDone: string;
  totalCount: number;
  loadStatus: LoadStatus;
  loadError: string;
  onRetry: () => void;
  currencyOptions: string[];
  sourceOptions: string[];
  search: string; onSearch: (v: string) => void;
  currency: string; onCurrency: (v: string) => void;
  source: string; onSource: (v: string) => void;
  status: string; onStatus: (v: string) => void;
  selectedId?: string;
  onRowClick: (r: FxRate) => void;
}) {
  const [publishOpen, setPublishOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);
  const today = todayIso();
  // One card per currency with a rate IN FORCE today (the backend's
  // exactly-one-row rule, mirrored by rateInForce; none or two -> no card).
  const cards = derivedOptions(allRates, (r) => r.currency)
    .map((code) => {
      const value = rateInForce(allRates, code, today);
      const row = value === null ? undefined : allRates.find((r) =>
        r.active && r.currency === code && r.rate === value
        && (!r.effectiveFrom || r.effectiveFrom <= today) && (!r.effectiveTo || r.effectiveTo >= today));
      return value === null ? null : { code, value, from: row?.effectiveFrom ?? null };
    })
    .filter((c): c is { code: string; value: number; from: string | null } => c !== null);

  return (
    <div data-testid="fx-tab" style={{ display: "flex", flexDirection: "column" }}>
      {/* The customs note: ONE line, the rest behind "More" (settings-fx-layout). */}
      <div data-testid="fx-info-bar" className="flex items-start"
           style={{ gap: spacing[6], marginBottom: spacing[12], fontSize: typography.fontSize.sm,
                    color: semanticColors.textSecondary }}>
        <Info size={iconSize.md} strokeWidth={iconStroke.regular} color={colors.indigo600}
              style={{ flexShrink: 0, marginTop: spacing[2] }} />
        <span>
          Customs publishes notified exchange rates twice monthly.
          {noteOpen && (
            <> Each is effective from the following day. Duty is calculated on the rate at
              customs clearance, not the quote date.</>
          )}{" "}
          <button data-testid="fx-info-more" onClick={() => setNoteOpen((v) => !v)}
                  style={{ background: "none", border: "none", padding: 0, cursor: "pointer",
                           color: semanticColors.accentInk, fontSize: typography.fontSize.sm,
                           textDecoration: "underline" }}>
            {noteOpen ? "Less" : "More"}
          </button>
        </span>
      </div>

      {loadStatus === "error" && (
        <ReferenceError
          testId="fx-load-error"
          retryTestId="fx-retry"
          message="Could not load the exchange rates."
          detail={loadError}
          onRetry={onRetry}
        />
      )}

      {loadStatus === "loading" && (
        <ReferenceLoading testId="fx-loading" label="Loading exchange rates..." />
      )}

      {loadStatus === "ok" && totalCount === 0 && (
        <ReferenceEmpty
          testId="fx-empty"
          label="No exchange rates are on file. The schedule is maintained in Odoo."
        />
      )}

      {loadStatus === "ok" && canPublish && totalCount === 0 && (
        <PublishFxRate rates={allRates} onPublished={onPublished} />
      )}

      {/* FAIL LOUD: everything below renders only on a successful, non-empty
          read. A filter bar over an empty grid reads as "nothing matched". */}
      {loadStatus === "ok" && totalCount > 0 && (
      <>
      {/* Current rates: a compact row of cards, one per currency in force
          today, then the Admin's "Publish rate". Cards stack on a phone. */}
      <div data-testid="fx-current-rates" className="flex"
           style={{ gap: spacing[12], marginBottom: spacing[12], flexWrap: "wrap", alignItems: "stretch" }}>
        {cards.map((c) => (
          <div key={c.code} data-testid={`fx-current-rate-card-${c.code}`}
               style={{
                 flex: "1 1 200px", maxWidth: 240, padding: `${spacing[8]}px ${spacing[12]}px`,
                 backgroundColor: semanticColors.surface, border: `1px solid ${semanticColors.border}`,
                 borderRadius: radius.base, boxShadow: shadows.sm,
               }}>
            <div style={{ fontSize: typography.fontSize.xs, color: semanticColors.textSecondary }}>{c.code} → INR</div>
            <div data-testid={`fx-current-rate-value-${c.code}`} style={{
              fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.semibold,
              color: semanticColors.textPrimary, fontVariantNumeric: "tabular-nums",
            }}>
              {c.value.toFixed(2)}
            </div>
            <div style={{ fontSize: typography.fontSize.xs, color: semanticColors.textMuted }}>
              {c.from ? `From ${formatDate(c.from)}` : "Start date not recorded"}
            </div>
          </div>
        ))}
        {canPublish && (
          <div className="flex items-center">
            <button data-testid="fx-publish-open"
                    onClick={() => setPublishOpen((v) => !v)}
                    style={{
                      height: heights.control, padding: `0 ${spacing[14]}px`, border: "none",
                      borderRadius: radius.base, backgroundColor: colors.indigo600,
                      color: semanticColors.onAccent, fontSize: typography.fontSize.base,
                      fontWeight: typography.fontWeight.medium, cursor: "pointer", whiteSpace: "nowrap",
                    }}>
              {publishOpen ? "Close" : "Publish rate"}
            </button>
          </div>
        )}
      </div>

      {/* PUBLISHING, ADMIN ONLY: the existing form, rules and wording
          unchanged, in an inline expander. */}
      {canPublish && publishOpen && (
        <PublishFxRate rates={allRates} onPublished={onPublished} />
      )}

      {correctDone !== "" && (
        <div data-testid="fx-correct-done"
             style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary, marginBottom: spacing[8] }}>
          {correctDone}
        </div>
      )}

      {/* Search and filters directly above the history. Wraps on a phone. */}
      <div
        data-testid="fx-filter-bar"
        className="flex items-center"
        style={{
          minHeight: heights.filterBar, padding: `${spacing[6]}px ${spacing[12]}px`, marginBottom: spacing[8],
          backgroundColor: semanticColors.panelBg, flexWrap: "wrap",
          border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
          gap: spacing[12],
        }}
      >
        <div
          className="flex items-center"
          style={{
            flex: 1, minWidth: 180,
            height: heights.input, padding: `0 ${spacing[8]}px`,
            backgroundColor: semanticColors.surface,
            border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
            gap: spacing[6],
          }}
        >
          <Search size={iconSize.md} strokeWidth={iconStroke.regular} color={semanticColors.textPlaceholder} />
          <input
            data-testid="fx-search"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            placeholder="Search notes"
            style={{
              flex: 1, minWidth: 0, height: "100%",
              fontSize: typography.fontSize.base, color: semanticColors.textPrimary,
              background: "transparent", border: "none", outline: "none",
            }}
          />
        </div>
        {/* Hidden when the derived set is empty: a dropdown holding only
            "All" invites a click that can change nothing. */}
        {currencyOptions.length > 0 && (
          <select data-testid="fx-filter-currency" value={currency}
                  onChange={(e) => onCurrency(e.target.value)} style={SELECT_STYLE}>
            <option value="All">Currency: All</option>
            {currencyOptions.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        )}
        {sourceOptions.length > 0 && (
          <select data-testid="fx-filter-source" value={source}
                  onChange={(e) => onSource(e.target.value)} style={SELECT_STYLE}>
            <option value="All">Source: All</option>
            {sourceOptions.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
        <select data-testid="fx-filter-status" value={status}
                onChange={(e) => onStatus(e.target.value)} style={SELECT_STYLE}>
          {STATUS_OPTS.map((s) => <option key={s} value={s}>{s === "All" ? "Status: All" : s}</option>)}
        </select>
        <span style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary, whiteSpace: "nowrap" }}>
          <span data-testid="fx-filtered-count" className="tabular-nums">{rates.length}</span>
          {rates.length !== totalCount ? (
            <> of <span className="tabular-nums">{totalCount}</span> rates</>
          ) : (
            <> rates</>
          )}
        </span>
      </div>

      <FxRatesTable rows={rates} onRowClick={onRowClick} selectedId={selectedId} onCorrect={onCorrect} />
      </>
      )}
    </div>
  );
}

// ---------- Tab 2: Risk Buffers ----------------------------------------------

// app review P2-12 (owner 2026-09-29): NOT IN USE, AND SAID SO. The engine
// has no buffer input, so these values affect no price. The tab used to show
// sliders, a disabled Save and an INVENTED change history ("R. Sharma",
// "A. Mehta") as if it were real -- all removed. What remains is the values
// this build carries, read-only.
function BufferValue({ label, value, helper, testid }: {
  label: string; value: number; helper: string; testid: string;
}) {
  return (
    <div data-testid={testid} style={{ display: "flex", flexDirection: "column", gap: spacing[4] }}>
      <div className="flex items-center justify-between">
        <span style={{ fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium, color: semanticColors.textPrimary }}>
          {label}
        </span>
        <span data-testid={`${testid}-value`} style={{
          fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.semibold,
          color: semanticColors.textSecondary, fontVariantNumeric: "tabular-nums",
        }}>
          {value.toFixed(1)}%
        </span>
      </div>
      <span style={{ fontSize: typography.fontSize.xs, color: semanticColors.textMuted }}>{helper}</span>
    </div>
  );
}

function RiskBuffersTab() {
  const settings = api.getRiskBufferSettings();
  return (
    <div style={{ flex: 1, minHeight: 0 }}>
      <InfoBar testid="buffers-not-in-use">
        Not in use yet — buffers don&rsquo;t affect prices.
      </InfoBar>
      <div
        data-testid="buffers-settings-block"
        style={{
          display: "flex", flexDirection: "column", gap: spacing[16],
          padding: spacing[16], backgroundColor: semanticColors.surface,
          border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
        }}
      >
        <BufferValue testid="buffer-fx" label="FX Risk Buffer" value={settings.fxRiskBufferPct}
                     helper="Meant for the customs rate on imported items." />
        <BufferValue testid="buffer-freight" label="Freight Risk Buffer" value={settings.freightRiskBufferPct}
                     helper="Meant to absorb carrier rate increases before delivery." />
        <BufferValue testid="buffer-negotiation" label="Negotiation Buffer" value={settings.negotiationBufferPct}
                     helper="Meant as headroom for post-order discount requests." />
      </div>
    </div>
  );
}

// ---------- Tab 3: Company ----------------------------------------------------

// Which of the three logo states the next save will express. "keep" is the
// default and the safe one: editing a phone number must never re-upload or
// drop the image. See api.ts's "Company settings" note.
type LogoAction = "keep" | "replace" | "remove";

function CompanyTab() {
  // Seeded from the mock so defaultWarehouse / defaultQuoteValidityDays have
  // values — the backend stores neither, so those two stay local. The five
  // fields it does store are overwritten from the API on load.
  const [form, setForm] = useState<CompanySettings>(() => api.getCompanySettings());
  // ⚠️ COSMETIC ONLY. save_company_settings is Admin-only on the BACKEND,
  // which is what actually enforces it; nothing here is a gate, and a bug in
  // this line can only ever show a control the server then refuses.
  //
  // ⚠️ IT NOW DRIVES THE FIELDS, NOT JUST THE BUTTON, AND THAT WAS A REAL
  // DEFECT. Hiding Save alone left every input editable: a Sales user opened
  // Company, typed a new GSTIN, found no Save button, navigated away, and
  // their edits vanished with nothing said. Worse, a partially-retyped
  // address sat on screen looking like the stored one. Read-only fields plus
  // one plain sentence is the honest version -- see NON_ADMIN_NOTE.
  const canSaveCompany = useCanEditSettings();
  const set = <K extends keyof CompanySettings>(k: K, v: CompanySettings[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  const [loadStatus, setLoadStatus] = useState<"loading" | "ok" | "error">("loading");
  const [loadError, setLoadError] = useState("");

  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [saveError, setSaveError] = useState("");

  // What is on screen now: the stored logo, a newly picked one, or nothing.
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoAction, setLogoAction] = useState<LogoAction>("keep");
  const [logoError, setLogoError] = useState("");
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const applySettings = useCallback((s: BackendCompanySettings) => {
    setForm((f) => ({
      ...f,
      companyName: s.company_name,
      gstin: s.gstin,
      registeredAddress: s.address,
      contactEmail: s.contact_email,
      contactPhone: s.contact_phone,
      quoteValidityDays: s.quote_validity_days ? String(s.quote_validity_days) : "",
      approvalGpThreshold: s.approval_gp_threshold ? String(s.approval_gp_threshold) : "",
      // warehouse-master: the STORED default (an id), "" = not set.
      defaultWarehouse: s.default_warehouse_id ? String(s.default_warehouse_id) : "",
      // Configurable identity: undefined when an older backend sent none, so
      // the save below leaves them out.
      quotePrefix: s.quote_prefix ?? undefined,
      quotePadding: s.quote_padding != null ? String(s.quote_padding) : undefined,
      nextQuoteNumber: s.next_quote_number ?? undefined,
      oemOrderPrefix: s.oem_order_prefix ?? undefined,
      oemOrderPadding: s.oem_order_padding != null ? String(s.oem_order_padding) : undefined,
      nextOemOrderNumber: s.next_oem_order_number ?? undefined,
      // M4 (D195): GRN and delivery-note numbering, the same shape.
      grnPrefix: s.grn_prefix ?? undefined,
      grnPadding: s.grn_padding != null ? String(s.grn_padding) : undefined,
      nextGrnNumber: s.next_grn_number ?? undefined,
      dnPrefix: s.dn_prefix ?? undefined,
      dnPadding: s.dn_padding != null ? String(s.dn_padding) : undefined,
      nextDnNumber: s.next_dn_number ?? undefined,
      ourEntityAliases: s.our_entity_aliases,
      supplierCodeLabel: s.supplier_code_label,
      locationCodeLabel: s.location_code_label,
      poProfileOracle: s.po_profile_oracle,
      defaultGstRate: s.default_gst_rate != null ? String(s.default_gst_rate) : undefined,
      moneyLocale: s.money_locale || undefined,
      currencySymbol: s.currency_symbol || undefined,
      amountWords: s.amount_words || undefined,
      productName: s.product_name ?? undefined,
    }));
    setLogoPreview(s.logo_data_url);
    // Back in sync with the server, so the next save leaves the logo alone
    // again unless the user touches it.
    setLogoAction("keep");
  }, []);

  const load = useCallback(async () => {
    setLoadStatus("loading");
    setLoadError("");

    const result = await api.getCompanySettingsFromBackend();
    if (!result.ok || !result.settings) {
      setLoadError(result.error ?? "Backend reported an unknown failure.");
      setLoadStatus("error");
      return;
    }
    applySettings(result.settings);
    setLoadStatus("ok");
  }, [applySettings]);

  useEffect(() => { load(); }, [load]);

  // warehouse-master: the options for "Default dispatch warehouse".
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  useEffect(() => {
    let live = true;
    void getWarehouses().then((r) => { if (live && r.ok && r.warehouses) setWarehouses(r.warehouses); });
    return () => { live = false; };
  }, []);

  function onPickFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    // Reset immediately so picking the same file twice still fires onChange.
    e.target.value = "";
    if (!file) return;

    setLogoError("");
    setSaveStatus("idle");

    if (!ACCEPTED_LOGO_TYPES.includes(file.type)) {
      setLogoError(
        `${file.name} is a ${file.type || "unknown"} file. Use a PNG, JPEG, GIF or WEBP image.`,
      );
      return;
    }
    // Caught here so an oversized file never leaves the browser. The
    // backend enforces the same limit regardless.
    if (file.size > MAX_LOGO_BYTES) {
      const mb = (file.size / (1024 * 1024)).toFixed(1);
      setLogoError(
        `${file.name} is ${mb}MB, over the ${MAX_LOGO_BYTES / (1024 * 1024)}MB limit. Pick a smaller image.`,
      );
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => setLogoError(`Could not read ${file.name}.`);
    reader.onload = () => {
      // A data URL, which is exactly what the endpoint accepts.
      setLogoPreview(String(reader.result));
      setLogoAction("replace");
    };
    reader.readAsDataURL(file);
  }

  function removeLogo() {
    setLogoPreview(null);
    setLogoAction("remove");
    setLogoError("");
    setSaveStatus("idle");
  }

  async function save() {
    setSaveStatus("saving");
    setSaveError("");

    const result = await api.saveCompanySettingsToBackend({
      companyName: form.companyName,
      gstin: form.gstin,
      address: form.registeredAddress,
      contactEmail: form.contactEmail,
      contactPhone: form.contactPhone,
      quoteValidityDays: form.quoteValidityDays ?? "",
      approvalGpThreshold: form.approvalGpThreshold ?? "",
      defaultWarehouseId: form.defaultWarehouse ? Number(form.defaultWarehouse) : null,
      quotePrefix: form.quotePrefix,
      quotePadding: form.quotePadding,
      oemOrderPrefix: form.oemOrderPrefix,
      oemOrderPadding: form.oemOrderPadding,
      grnPrefix: form.grnPrefix,
      grnPadding: form.grnPadding,
      dnPrefix: form.dnPrefix,
      dnPadding: form.dnPadding,
      ourEntityAliases: form.ourEntityAliases,
      supplierCodeLabel: form.supplierCodeLabel,
      locationCodeLabel: form.locationCodeLabel,
      poProfileOracle: form.poProfileOracle,
      defaultGstRate: form.defaultGstRate,
      moneyLocale: form.moneyLocale,
      currencySymbol: form.currencySymbol,
      amountWords: form.amountWords,
      productName: form.productName,
      // The whole point of tracking logoAction: only mention the logo when
      // it changed. Untouched means the key is absent from the request.
      logoBase64:
        logoAction === "replace" ? logoPreview
        : logoAction === "remove" ? null
        : undefined,
    });

    if (!result.ok || !result.settings) {
      // Never "Saved" when it wasn't, and the local edits stay on screen so
      // nothing the user typed is lost to a failed request.
      setSaveError(result.error ?? "Backend reported an unknown failure.");
      setSaveStatus("error");
      return;
    }

    // Repopulate from what the backend actually stored.
    applySettings(result.settings);
    setSaveStatus("saved");
  }

  const busy = loadStatus === "loading" || saveStatus === "saving";

  return (
    <div style={{ flex: 1, minHeight: 0 }}>
      <div
        data-testid="company-form"
        style={{
          maxWidth: 640, padding: spacing[16],
          backgroundColor: semanticColors.surface, border: `1px solid ${semanticColors.border}`,
          borderRadius: radius.base, boxShadow: shadows.sm,
        }}
      >
        {loadStatus === "loading" && (
          <div data-testid="company-loading"
               style={{ fontSize: typography.fontSize.sm, color: semanticColors.textPlaceholder, marginBottom: spacing[14] }}>
            Loading company settings…
          </div>
        )}

        {loadStatus === "error" && (
          <div data-testid="company-load-error" className="flex items-start"
               style={{
                 marginBottom: spacing[14], padding: spacing[12], gap: spacing[8],
                 border: `1px solid ${semanticColors.destructiveBorder}`,
                 backgroundColor: semanticColors.destructiveBg, borderRadius: radius.base,
                 fontSize: typography.fontSize.sm, color: semanticColors.destructive,
               }}>
            <AlertCircle size={iconSize.md} strokeWidth={iconStroke.regular} style={{ flexShrink: 0, marginTop: spacing[2] }} />
            <span>
              Could not load saved settings, so the fields below are local defaults
              rather than what is stored. {loadError}
            </span>
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[14] }}>
          <TextField label="Company Name" testid="company-name" value={form.companyName} onChange={(v) => set("companyName", v)} readOnly={!canSaveCompany} />
          <div style={{ display: "flex", flexDirection: "column" }}>
            <TextField label="GSTIN" testid="company-gstin" value={form.gstin} onChange={(v) => set("gstin", v)} readOnly={!canSaveCompany} />
            {/* ADVISORY ONLY, and deliberately not a blocker. A GSTIN this
                app refuses to store is worse than one it stores with a
                warning: the format is a 15-character pattern that real edge
                cases do occasionally sit outside, and the authority on it is
                the tax office, not this form. */}
            {form.gstin.trim() !== "" && !GSTIN_RE.test(form.gstin.trim()) && (
              <span
                data-testid="company-gstin-warning"
                style={{
                  fontSize: typography.fontSize.xs, color: colors.amber600,
                  marginTop: spacing[4],
                }}
              >
                Does not look like a 15-character GSTIN. It will still be saved.
              </span>
            )}
          </div>
        </div>

        <div style={{ marginTop: spacing[14] }}>
          <div style={{ display: "flex", flexDirection: "column", gridColumn: "1 / -1" }}>
            <FieldLabel>Registered Address</FieldLabel>
            {/* Multiline: the address prints across several lines on the quote
                document, so it has to be enterable that way. */}
            <textarea
              data-testid="company-address"
              value={form.registeredAddress}
              readOnly={!canSaveCompany}
              aria-readonly={!canSaveCompany || undefined}
              onChange={(e) => set("registeredAddress", e.target.value)}
              rows={3}
              style={{
                ...(canSaveCompany ? INPUT : READONLY_INPUT),
                height: "auto", resize: "vertical",
                paddingTop: spacing[8], paddingBottom: spacing[8],
              }}
            />
          </div>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[14], marginTop: spacing[14] }}>
          <TextField label="Contact Email" testid="company-email" value={form.contactEmail} onChange={(v) => set("contactEmail", v)} readOnly={!canSaveCompany} />
          <TextField label="Contact Phone" testid="company-phone" value={form.contactPhone} onChange={(v) => set("contactPhone", v)} readOnly={!canSaveCompany} />
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[14], marginTop: spacing[14] }}>
          <div style={{ display: "flex", flexDirection: "column" }}>
            {/* warehouse-master (2026-10-01): STORED. New drafts are dispatched
                from it unless one is chosen. Empty by default. FTWZ sites show
                greyed: their tax treatment is not configured yet. */}
            <FieldLabel>Default dispatch warehouse</FieldLabel>
            <select data-testid="company-warehouse" value={form.defaultWarehouse}
                    disabled={!canSaveCompany}
                    onChange={(e) => set("defaultWarehouse", e.target.value)}
                    style={canSaveCompany ? INPUT : READONLY_INPUT}>
              <option value="">Not set</option>
              {warehouses
                .filter((w) => w.active || String(w.id) === form.defaultWarehouse)
                .map((w) => (
                  <option key={w.id} value={String(w.id)} disabled={!w.selectable}
                          data-testid={`company-warehouse-option-${w.code}`}>
                    {warehouseOptionLabel(w)}
                  </option>
                ))}
            </select>
          </div>
          {/* QUOTE VALIDITY -- STORED (quote-validity, 2026-09-23). Replaced a
              browser-local select that defaulted to an invented 30 days. Empty
              by default, and empty prints nothing. Read at finalise only:
              changing it never moves a quote already finalised. */}
          <div style={{ display: "flex", flexDirection: "column" }}>
            <FieldLabel>Quote validity (days)</FieldLabel>
            <input data-testid="company-validity-days"
                   type="number" min={0} max={3650} step={1} placeholder="Not set"
                   value={form.quoteValidityDays ?? ""}
                   readOnly={!canSaveCompany}
                   onChange={(e) => set("quoteValidityDays", e.target.value.replace(/[^0-9]/g, ""))}
                   style={canSaveCompany ? INPUT : READONLY_INPUT} />
          </div>
          {/* MARGIN APPROVAL (margin-approval, 2026-09-23). Admin only, EMPTY by
              default; while empty, approval cannot apply. A Sales user finalising
              a quote whose GP % is below this must request an Admin's approval. */}
          <div style={{ display: "flex", flexDirection: "column" }}>
            <FieldLabel>Approval required below GP %</FieldLabel>
            <input data-testid="company-approval-gp"
                   type="number" min={0} max={100} step={0.1} placeholder="Not set"
                   value={form.approvalGpThreshold ?? ""}
                   readOnly={!canSaveCompany}
                   onChange={(e) => set("approvalGpThreshold", e.target.value.replace(/[^0-9.]/g, ""))}
                   style={canSaveCompany ? INPUT : READONLY_INPUT} />
          </div>
        </div>

        {canSaveCompany && !(form.approvalGpThreshold ?? "").trim() && (
          <div data-testid="company-approval-unset" role="note" style={{
            marginTop: spacing[12], padding: `${spacing[8]}px ${spacing[12]}px`,
            backgroundColor: semanticColors.warningBg, border: `1px solid ${semanticColors.warningBorder}`,
            color: semanticColors.warningTextOnTint, borderRadius: radius.base, fontSize: typography.fontSize.sm,
          }}>
            Margin approval is on but no GP % is set, so no quote needs approval yet.
          </div>
        )}

        {/* QUOTE NUMBERING (configurable identity P1-5, 2026-10-29). Stored on
            Odoo's own sequence. The preview is read without drawing a number;
            a change applies from the next finalised quote and never renames
            one already issued. */}
        {form.quotePrefix !== undefined && (
          <div data-testid="company-numbering" style={{ marginTop: spacing[20], paddingTop: spacing[16], borderTop: `1px solid ${semanticColors.border}` }}>
            <FieldLabel>Quote numbering</FieldLabel>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[14], marginTop: spacing[8] }}>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <FieldLabel>Prefix</FieldLabel>
                <input data-testid="company-quote-prefix" value={form.quotePrefix ?? ""} maxLength={32}
                       readOnly={!canSaveCompany} onChange={(e) => set("quotePrefix", e.target.value)}
                       style={canSaveCompany ? INPUT : READONLY_INPUT} />
              </div>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <FieldLabel>Digits</FieldLabel>
                <input data-testid="company-quote-padding" type="number" min={1} max={10} step={1}
                       value={form.quotePadding ?? ""} readOnly={!canSaveCompany}
                       onChange={(e) => set("quotePadding", e.target.value.replace(/[^0-9]/g, ""))}
                       style={canSaveCompany ? INPUT : READONLY_INPUT} />
              </div>
            </div>
            <div data-testid="company-next-quote-number" style={{ marginTop: spacing[8], fontSize: typography.fontSize.sm, color: semanticColors.textSecondary }}>
              Next quote number: <span style={{ fontVariantNumeric: "tabular-nums", color: semanticColors.textPrimary }}>{form.nextQuoteNumber ?? "—"}</span>
              {canSaveCompany && " · %(year)s prints the year. A change applies from the next finalised quote; issued numbers never change."}
            </div>
          </div>
        )}

        {/* OEM ORDER NUMBERING (Order Management O5, D181 #9). The same
            pattern as quote numbering: Odoo's own sequence, previewed without
            drawing; a number is drawn only when an OEM order is placed. */}
        {form.oemOrderPrefix !== undefined && (
          <div data-testid="company-oem-numbering" style={{ marginTop: spacing[20], paddingTop: spacing[16], borderTop: `1px solid ${semanticColors.border}` }}>
            <FieldLabel>OEM order numbering</FieldLabel>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[14], marginTop: spacing[8] }}>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <FieldLabel>Prefix</FieldLabel>
                <input data-testid="company-oem-order-prefix" value={form.oemOrderPrefix ?? ""} maxLength={32}
                       readOnly={!canSaveCompany} onChange={(e) => set("oemOrderPrefix", e.target.value)}
                       style={canSaveCompany ? INPUT : READONLY_INPUT} />
              </div>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <FieldLabel>Digits</FieldLabel>
                <input data-testid="company-oem-order-padding" type="number" min={1} max={10} step={1}
                       value={form.oemOrderPadding ?? ""} readOnly={!canSaveCompany}
                       onChange={(e) => set("oemOrderPadding", e.target.value.replace(/[^0-9]/g, ""))}
                       style={canSaveCompany ? INPUT : READONLY_INPUT} />
              </div>
            </div>
            <div data-testid="company-next-oem-order-number" style={{ marginTop: spacing[8], fontSize: typography.fontSize.sm, color: semanticColors.textSecondary }}>
              Next OEM order number: <span style={{ fontVariantNumeric: "tabular-nums", color: semanticColors.textPrimary }}>{form.nextOemOrderNumber ?? "—"}</span>
              {canSaveCompany && " · A change applies from the next OEM order placed; issued numbers never change."}
            </div>
          </div>
        )}

        {/* GRN AND DELIVERY NOTE NUMBERING (M4, D195): the OEM order pattern.
            Previewed without drawing; a number is drawn only when a goods
            receipt or a delivery note is recorded. */}
        {([["grn", "Goods receipt (GRN) numbering", "Next GRN number", "goods receipt"],
          ["dn", "Delivery note numbering", "Next delivery note number", "delivery note"]] as const).map(([key, title, nextLabel, what]) => {
          const prefixKey = `${key}Prefix` as "grnPrefix" | "dnPrefix";
          const paddingKey = `${key}Padding` as "grnPadding" | "dnPadding";
          const next = key === "grn" ? form.nextGrnNumber : form.nextDnNumber;
          if (form[prefixKey] === undefined) return null;
          return (
            <div key={key} data-testid={`company-${key}-numbering`} style={{ marginTop: spacing[20], paddingTop: spacing[16], borderTop: `1px solid ${semanticColors.border}` }}>
              <FieldLabel>{title}</FieldLabel>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[14], marginTop: spacing[8] }}>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <FieldLabel>Prefix</FieldLabel>
                  <input data-testid={`company-${key}-prefix`} value={form[prefixKey] ?? ""} maxLength={32}
                         readOnly={!canSaveCompany} onChange={(e) => set(prefixKey, e.target.value)}
                         style={canSaveCompany ? INPUT : READONLY_INPUT} />
                </div>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <FieldLabel>Digits</FieldLabel>
                  <input data-testid={`company-${key}-padding`} type="number" min={1} max={10} step={1}
                         value={form[paddingKey] ?? ""} readOnly={!canSaveCompany}
                         onChange={(e) => set(paddingKey, e.target.value.replace(/[^0-9]/g, ""))}
                         style={canSaveCompany ? INPUT : READONLY_INPUT} />
                </div>
              </div>
              <div data-testid={`company-next-${key}-number`} style={{ marginTop: spacing[8], fontSize: typography.fontSize.sm, color: semanticColors.textSecondary }}>
                {nextLabel}: <span style={{ fontVariantNumeric: "tabular-nums", color: semanticColors.textPrimary }}>{next ?? "—"}</span>
                {canSaveCompany && ` · A change applies from the next ${what} recorded; issued numbers never change.`}
              </div>
            </div>
          );
        })}

        {/* TAX AND MONEY FORMAT (configurable identity P1-10 / P1-11). The GST
            rate applies to DRAFT lines with no product and to NEW products;
            an issued quote keeps the tax it was issued with. The format is
            display only. */}
        {form.defaultGstRate !== undefined && (
          <div data-testid="company-tax-money" style={{ marginTop: spacing[20], paddingTop: spacing[16], borderTop: `1px solid ${semanticColors.border}` }}>
            <FieldLabel>Tax and money format</FieldLabel>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[14], marginTop: spacing[8] }}>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <FieldLabel>Default GST rate (%)</FieldLabel>
                <input data-testid="company-default-gst" type="number" min={0} max={100} step={0.01}
                       value={form.defaultGstRate ?? ""} readOnly={!canSaveCompany}
                       onChange={(e) => set("defaultGstRate", e.target.value.replace(/[^0-9.]/g, ""))}
                       style={canSaveCompany ? INPUT : READONLY_INPUT} />
              </div>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <FieldLabel>Number format (locale)</FieldLabel>
                <input data-testid="company-money-locale" value={form.moneyLocale ?? ""} maxLength={16}
                       readOnly={!canSaveCompany} onChange={(e) => set("moneyLocale", e.target.value)}
                       style={canSaveCompany ? INPUT : READONLY_INPUT} />
              </div>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <FieldLabel>Currency symbol</FieldLabel>
                <input data-testid="company-currency-symbol" value={form.currencySymbol ?? ""} maxLength={6}
                       readOnly={!canSaveCompany} onChange={(e) => set("currencySymbol", e.target.value)}
                       style={canSaveCompany ? INPUT : READONLY_INPUT} />
              </div>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <FieldLabel>Amount in words</FieldLabel>
                <select data-testid="company-amount-words" value={form.amountWords ?? "indian"}
                        disabled={!canSaveCompany} onChange={(e) => set("amountWords", e.target.value)}
                        style={canSaveCompany ? INPUT : READONLY_INPUT}>
                  <option value="indian">Lakh / Crore</option>
                  <option value="international">Thousand / Million</option>
                </select>
              </div>
            </div>
          </div>
        )}

        {/* CUSTOMER POs AND LABELS (configurable identity P1-6/P1-7). */}
        {form.supplierCodeLabel !== undefined && (
          <div data-testid="company-identity" style={{ marginTop: spacing[20], paddingTop: spacing[16], borderTop: `1px solid ${semanticColors.border}` }}>
            <FieldLabel>Customer POs and labels</FieldLabel>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: spacing[14], marginTop: spacing[8] }}>
              <div style={{ display: "flex", flexDirection: "column", gridColumn: "1 / -1" }}>
                <FieldLabel>Our entity names on customer POs (one per line)</FieldLabel>
                <textarea data-testid="company-entity-aliases" rows={3} value={form.ourEntityAliases ?? ""}
                          readOnly={!canSaveCompany} onChange={(e) => set("ourEntityAliases", e.target.value)}
                          style={{ ...(canSaveCompany ? INPUT : READONLY_INPUT), height: "auto", padding: spacing[8] }} />
              </div>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <FieldLabel>Supplier part number label</FieldLabel>
                <input data-testid="company-supplier-code-label" value={form.supplierCodeLabel ?? ""} maxLength={40}
                       readOnly={!canSaveCompany} onChange={(e) => set("supplierCodeLabel", e.target.value)}
                       style={canSaveCompany ? INPUT : READONLY_INPUT} />
              </div>
              <div style={{ display: "flex", flexDirection: "column" }}>
                <FieldLabel>Warehouse location code label</FieldLabel>
                <input data-testid="company-location-code-label" value={form.locationCodeLabel ?? ""} maxLength={40}
                       readOnly={!canSaveCompany} onChange={(e) => set("locationCodeLabel", e.target.value)}
                       style={canSaveCompany ? INPUT : READONLY_INPUT} />
              </div>
              <div style={{ display: "flex", flexDirection: "column", gridColumn: "1 / -1" }}>
                <FieldLabel>Product name (shown after sign-in; empty = the build's own name)</FieldLabel>
                <input data-testid="company-product-name" value={form.productName ?? ""} maxLength={40}
                       readOnly={!canSaveCompany} onChange={(e) => set("productName", e.target.value)}
                       style={canSaveCompany ? INPUT : READONLY_INPUT} />
              </div>
              <label className="flex items-center" style={{ gap: spacing[8], gridColumn: "1 / -1", fontSize: typography.fontSize.base, color: semanticColors.textPrimary }}>
                <input data-testid="company-po-profile-oracle" type="checkbox" checked={!!form.poProfileOracle}
                       disabled={!canSaveCompany} onChange={(e) => set("poProfileOracle", e.target.checked)} />
                Read customer POs printed in the Oracle purchase-order layout
              </label>
            </div>
          </div>
        )}

        {/* Logo ------------------------------------------------------- */}
        <div style={{ marginTop: spacing[20], paddingTop: spacing[16], borderTop: `1px solid ${semanticColors.border}` }}>
          <FieldLabel>Company Logo</FieldLabel>
          <div className="flex items-start" style={{ gap: spacing[16], marginTop: spacing[8] }}>
            <div
              data-testid="company-logo-preview"
              className="flex items-center justify-center"
              style={{
                width: layout.logoPreviewBox, height: layout.logoPreviewBox,
                border: `1px dashed ${semanticColors.border}`,
                borderRadius: radius.base,
                backgroundColor: semanticColors.panelBg,
                flexShrink: 0, overflow: "hidden",
              }}
            >
              {logoPreview ? (
                <img
                  data-testid="company-logo-img"
                  src={logoPreview}
                  alt="Company logo"
                  style={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
                />
              ) : (
                <span style={{ fontSize: typography.fontSize.xs, color: semanticColors.textPlaceholder }}>
                  No logo
                </span>
              )}
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <input
                ref={fileInputRef}
                data-testid="company-logo-input"
                type="file"
                accept={ACCEPTED_LOGO_TYPES.join(",")}
                onChange={onPickFile}
                style={{ display: "none" }}
              />
              <div className="flex items-center" style={{ gap: spacing[8] }}>
                {/* A non-admin cannot upload a logo either -- the same
                    endpoint stores it, and the same 403 refuses it. */}
                {canSaveCompany && <button
                  data-testid="company-logo-choose"
                  className="btn-secondary"
                  disabled={busy}
                  onClick={() => fileInputRef.current?.click()}
                  style={{
                    height: heights.control, padding: `0 ${spacing[12]}px`,
                    backgroundColor: semanticColors.surface, color: semanticColors.textSecondary,
                    border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
                    fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium,
                    cursor: "pointer", display: "inline-flex", alignItems: "center", gap: spacing[6],
                    opacity: busy ? 0.6 : 1,
                  }}
                >
                  <Upload size={iconSize.md} strokeWidth={iconStroke.regular} />
                  {logoPreview ? "Replace" : "Upload"}
                </button>}
                {canSaveCompany && logoPreview && (
                  <button
                    data-testid="company-logo-remove"
                    className="btn-secondary"
                    disabled={busy}
                    onClick={removeLogo}
                    style={{
                      height: heights.control, padding: `0 ${spacing[12]}px`,
                      backgroundColor: semanticColors.surface, color: semanticColors.destructive,
                      border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
                      fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium,
                      cursor: "pointer", opacity: busy ? 0.6 : 1,
                    }}
                  >
                    Remove
                  </button>
                )}
              </div>

              {canSaveCompany && (
                <div style={{ marginTop: spacing[6], fontSize: typography.fontSize.xs, color: semanticColors.textMuted }}>
                  PNG, JPEG, GIF or WEBP, up to {MAX_LOGO_BYTES / (1024 * 1024)}MB.
                  {logoAction === "keep"
                    ? " Saving text changes leaves the stored logo alone."
                    : logoAction === "replace"
                      ? " New logo will be uploaded when you save."
                      : " Logo will be removed when you save."}
                </div>
              )}

              {logoError && (
                <div data-testid="company-logo-error"
                     style={{ marginTop: spacing[6], fontSize: typography.fontSize.sm, color: semanticColors.destructive }}>
                  {logoError}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* P3-10: sticky to the bottom of the page scroller, so Save is
            never below the fold at 1366. */}
        <div data-testid="company-actions" className="flex items-center" style={{
          marginTop: spacing[20], gap: spacing[12], position: "sticky", bottom: 0, zIndex: 2,
          backgroundColor: semanticColors.pageBg, paddingTop: spacing[8], paddingBottom: spacing[8],
          borderTop: `1px solid ${semanticColors.border}`,
        }}>
          {!canSaveCompany && (
            <span
              data-testid="company-readonly-note"
              style={{ fontSize: typography.fontSize.sm, color: semanticColors.textMuted }}
            >
              {NON_ADMIN_NOTE}
            </span>
          )}
          {canSaveCompany && <button
            data-testid="save-company-btn"
            className="btn-primary"
            disabled={busy}
            onClick={save}
            style={{
              height: heights.control, padding: `0 ${spacing[14]}px`,
              backgroundColor: colors.indigo600, color: semanticColors.onAccent,
              border: "none", borderRadius: radius.base,
              fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium,
              cursor: "pointer", opacity: busy ? 0.6 : 1,
            }}
          >
            {saveStatus === "saving" ? "Saving…" : "Save"}
          </button>}

          {saveStatus === "saved" && (
            <span data-testid="company-save-ok" className="flex items-center"
                  style={{ gap: spacing[6], fontSize: typography.fontSize.sm, color: semanticColors.success }}>
              <CheckCircle2 size={iconSize.md} strokeWidth={iconStroke.regular} />
              Saved to Odoo.
            </span>
          )}
        </div>

        {saveStatus === "error" && (
          <div data-testid="company-save-error" className="flex items-start"
               style={{
                 marginTop: spacing[12], padding: spacing[12], gap: spacing[8],
                 border: `1px solid ${semanticColors.destructiveBorder}`,
                 backgroundColor: semanticColors.destructiveBg, borderRadius: radius.base,
                 fontSize: typography.fontSize.sm, color: semanticColors.destructive,
               }}>
            <AlertCircle size={iconSize.md} strokeWidth={iconStroke.regular} style={{ flexShrink: 0, marginTop: spacing[2] }} />
            <span>Not saved. {saveError}</span>
          </div>
        )}

        <div style={{ marginTop: spacing[12], fontSize: typography.fontSize.xs, color: semanticColors.textMuted }}>
          The default dispatch warehouse applies to drafts created from now on.
          Quote validity applies to quotes finalised from now on.
        </div>
      </div>
    </div>
  );
}
